import React, { useState, useEffect, useRef } from "react";

export default function Navbar({
  darkMode,
  setDarkMode,
  user,
  userProfilePic,
  searchTerm,
  onSearch,
  toggleSidebar,
  isSidebarOpen,
}) {
  const [time, setTime] = useState(new Date());
  const dateInputRef = useRef(null);

  useEffect(() => {
    const timer = setInterval(() => setTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  const handleCalendarClick = () => {
    if (dateInputRef.current) {
      if (dateInputRef.current.showPicker) {
        dateInputRef.current.showPicker();
      } else {
        dateInputRef.current.click();
      }
    }
  };

  const userName = user?.displayName || user?.email?.split("@")[0] || "User";

  return (
    <header className="sticky top-0 z-[80] w-full shadow-sm">
      <nav className="h-20 border-b border-slate-100 dark:border-slate-800 bg-white/90 dark:bg-slate-900/90 backdrop-blur-md px-4 md:px-8 flex items-center justify-between gap-6">
        {/* Sidebar Toggle */}
        <button
          onClick={toggleSidebar}
          className="shrink-0 w-11 h-11 flex items-center justify-center rounded-2xl bg-indigo-600 text-white shadow-lg active:scale-95 transition-transform"
        >
          <i
            className={
              isSidebarOpen
                ? "fas fa-check-double text-lg"
                : "fas fa-bars text-lg"
            }
          ></i>
        </button>

        {/* Desktop Search */}
        <div className="hidden lg:flex flex-1 max-w-2xl items-center bg-slate-100 dark:bg-slate-800 px-5 py-3 rounded-2xl border border-transparent focus-within:border-indigo-500/30 transition-all shadow-inner">
          <i className="fas fa-search text-slate-400 mr-4"></i>
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => onSearch(e.target.value)}
            placeholder="Search your tasks and projects..."
            className="bg-transparent outline-none text-sm dark:text-white w-full font-bold"
          />
        </div>

        {/* CLOCK & CALENDAR TRIGGER */}
        <div
          onClick={handleCalendarClick}
          className="cursor-pointer group hidden sm:flex flex-col items-center px-6 border-x border-slate-100 dark:border-slate-800 shrink-0 hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors"
        >
          <input
            type="date"
            ref={dateInputRef}
            className="absolute opacity-0 pointer-events-none"
          />

          <span className="hidden xl:block text-[9px] font-black text-slate-400 group-hover:text-indigo-500 uppercase tracking-widest mb-0.5 transition-colors">
            {time.toLocaleDateString(undefined, {
              weekday: "short",
              month: "short",
              day: "numeric",
            })}
          </span>

          <div className="flex items-center gap-2">
            <i className="fas fa-calendar-alt text-indigo-500 text-[10px] xl:hidden group-hover:scale-110 transition-transform"></i>
            <span className="text-xs lg:text-sm font-black text-indigo-600 dark:text-indigo-400 tabular-nums">
              {time.toLocaleTimeString([], {
                hour: "2-digit",
                minute: "2-digit",
                second: "2-digit",
                hour12: true,
              })}
            </span>
          </div>
        </div>

        {/* Right Section */}
        <div className="flex items-center gap-4 shrink-0">
          <button
            onClick={() => setDarkMode(!darkMode)}
            className="w-10 h-10 text-slate-400 hover:text-indigo-500 transition-all"
          >
            <i
              className={
                darkMode ? "fas fa-sun text-lg" : "fas fa-moon text-lg"
              }
            ></i>
          </button>

          <div className="flex flex-col md:flex-row items-center gap-3">
            <div className="hidden md:flex flex-col items-end">
              <span className="text-[9px] font-black text-slate-400 uppercase tracking-tighter">
                Welcome,
              </span>
              <span className="text-xs font-black text-slate-900 dark:text-white capitalize truncate max-w-[80px]">
                {userName}
              </span>
            </div>
            <div className="h-11 w-11 relative">
              <img
                src={
                  userProfilePic ||
                  `https://ui-avatars.com/api/?name=${userName}&background=6366f1&color=fff`
                }
                className="w-11 h-11 rounded-full border-2 border-indigo-500 object-cover shadow-md"
                alt="Profile"
              />
              <span className="md:hidden absolute -bottom-6 left-1/2 -translate-x-1/2 text-[9px] font-black text-slate-400 uppercase">
                {userName}
              </span>
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile Search */}
      <div className="lg:hidden w-full bg-white/95 dark:bg-slate-900/95 px-4 py-3 border-b border-slate-100 dark:border-slate-800">
        <div className="flex items-center bg-slate-100 dark:bg-slate-800 px-4 py-3 rounded-xl">
          <i className="fas fa-search text-slate-400 mr-3"></i>
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => onSearch(e.target.value)}
            placeholder="Search tasks..."
            className="bg-transparent outline-none text-sm dark:text-white w-full font-bold"
          />
        </div>
      </div>
    </header>
  );
}
